import {
  amber,
  blue,
  darkYellow,
  green,
  lightDark,
  lightGreen,
  lightPurple,
  orange,
  orangeLight,
  purpleMedium,
  red,
  turquoise,
  white,
  yellow,
  yellowRGBA,
} from "./colors";

export function getUserId(user) {
  if (!user) return null;

  if (user.role === "employee") {
    return user.company_id;
  }

  if (user.type === "company") {
    return user.id;
  }

  return user.company_id;
}

export const formatCardDate = (input) => {
  let regexpExpiry = /^(?<month>0[1-9]|1[0-2])(?<year>[0-9]+)$/;
  let match = regexpExpiry.exec(input);

  return match ? `${match.groups.month}/${match.groups.year}` : input;
};

export const validateCardDate = (input) => {
  if (
    /^(?:(?:31(\/|-|\.)(?:0?[13578]|1[02]))\1|(?:(?:29|30)(\/|-|\.)(?:0?[13-9]|1[0-2])\2))(?:(?:1[6-9]|[2-9]\d)?\d{2})$|^(?:29(\/|-|\.)0?2\3(?:(?:(?:1[6-9]|[2-9]\d)?(?:0[48]|[2468][048]|[13579][26])|(?:(?:16|[2468][048]|[3579][26])00))))$|^(?:0?[1-9]|1\d|2[0-8])(\/|-|\.)(?:(?:0?[1-9])|(?:1[0-2]))\4(?:(?:1[6-9]|[2-9]\d)?\d{2})$/.test(
      input
    )
  ) {
    return true;
  }

  return false;
};

export const isValidFutureExpiry = (expiry) => {
  if (!/^\d{2}\/\d{4}$/.test(expiry)) return false; // must be MM/YYYY

  const [mm, yyyy] = expiry.split("/").map(Number);

  if (mm < 1 || mm > 12) return false;

  const current = new Date();
  const expiryDate = new Date(yyyy, mm - 1); // expiry is end of month

  const thisMonth = new Date(current.getFullYear(), current.getMonth());

  return expiryDate >= thisMonth;
};

export const colorSelector = (status) => {
  switch (status) {
    case "all":
      return lightPurple;
    case "new":
    case "scheduled":
      return white;
    case "en_route":
    case "En Route":
      return turquoise;
    case "started":
      return turquoise;
    case "arrived":
    case "Arrived":
      return orange;
    case "in progress":
    case "in_progress":
    case "POB":
      return blue;
    case "Cancelled":
    case "cancelled":
    case "SOS":
    case "<3h":
    case "client_canceled":
    case "driver_canceled":
    case "assign_failed":
    case "client_cancelled":
    case "provider_canceled":
    case "driver_cancelled":
    case "assigned_cancelled":
      return red;
    case "completed":
    case "Dest":
    case "24h+":
    case "ASAP":
      return green;
    case "drop_off":
      return purpleMedium;
    case "accepted":
      return orangeLight;
    case "scheduled_accepted":
      return lightGreen;
    case "pending":
      return darkYellow;
    case "timedout":
      return yellow;
    case "<24h":
      return amber;
    default:
      break;
  }
};

export const splitAndCapitalize = (inputString) => {
  if (typeof inputString !== "string") {
    return "";
  }
  // Split the string into words based on uppercase letters
  let words = inputString.toLowerCase().split("_");

  // Capitalize the first letter of each word
  let capitalizedWords = words.map(
    (word) => word.charAt(0).toUpperCase() + word.slice(1)
  );

  // Join the capitalized words into a new string
  let result = capitalizedWords.join(" ");

  return result;
};

// Title-case a backend service name so inconsistent casing (e.g. "Vert
// standard") renders "Vert Standard". Only uppercases word-initials, so
// acronyms like "MPV"/"ECX" are preserved. Returns "" for empty/non-strings.
export const formatServiceName = (name) =>
  typeof name === "string" && name.trim()
    ? name.replace(/\b\w/g, (c) => c.toUpperCase())
    : "";

export const ADMIN_EMAIL = "b.lane@vert.london";

const airportKeywords = ['airport', 'terminal', 'airfield'];

const airportIATACodes = [
  // London area (major + private)
  'LHR', // London Heathrow
  'LGW', // London Gatwick
  'LTN', // London Luton
  'STN', // London Stansted
  'LCY', // London City
  'SEN', // London Southend
  'BQH', // London Biggin Hill (private jets)
  'FAB', // Farnborough (private jets)
  'NHT', // RAF Northolt (private/military)

  // England - major
  'MAN', // Manchester
  'BHX', // Birmingham
  'EMA', // East Midlands
  'LPL', // Liverpool John Lennon
  'NCL', // Newcastle
  'LBA', // Leeds Bradford
  'CWL', // Cardiff (Wales)
  'BRS', // Bristol
  'EXT', // Exeter
  'SOU', // Southampton
  'BOH', // Bournemouth

  // England - private/regional
  'OXF', // Oxford Airport (private jets)
  'CVT', // Coventry Airport (private jets)
  'DSA', // Doncaster Sheffield (closed, still in some systems)
  'HLY', // Anglesey (Valley) – private/military
  'MSE', // Manston (currently closed, private ops)

  // Scotland
  'ABZ', // Aberdeen
  'GLA', // Glasgow
  'EDI', // Edinburgh
  'INV', // Inverness
  'PIK', // Glasgow Prestwick (also private)
  'WIC', // Wick John O'Groats (private)

  // Northern Ireland
  'BFS', // Belfast International
  'BHD', // Belfast City (George Best)
  'LDY', // City of Derry

  // Channel Islands (UK Crown Dependencies)
  'JER', // Jersey
  'GCI', // Guernsey
  'ACI', // Alderney
];

export const isAirportAddress = (address) => {
  if (!address) return false;
  const normalized = address.toLowerCase();

  const hasKeyword = airportKeywords.some((k) => {
    const regex = new RegExp(`\\b${k}\\b`, "i");
    return regex.test(normalized);
  });

  const hasIATACode = airportIATACodes.some((code) => {
    const regex = new RegExp(`\\b${code}\\b`, "i");
    return regex.test(address);
  });

  return hasKeyword || hasIATACode;
};

export const DRIVER_UPDATE_ERROR =
  "This ride cannot be accepted due to an invalid schedule or a conflict with another ride. Please review your current schedule and ensure there are no overlaps with previous or upcoming rides.";

export const truncate = (str, length = 15) => {
  if (!str || typeof str !== "string") return "";
  if (str.length <= length) return str;
  return str.slice(0, length).trim() + "...";
};

/**
 * Derives an IANA timezone string from a plain-text address string.
 * Detects UK, France, and Germany by matching country keywords/city names.
 * Falls back to 'Europe/London' (UK) if no match is found.
 */
export const getTimezoneFromAddress = (address) => {
  if (!address) return 'Europe/London';

  const norm = address.toLowerCase();

  // France — check before generic fallback
  if (
    norm.includes('france') ||
    norm.includes(', fr') ||
    norm.includes('paris') ||
    norm.includes('lyon') ||
    norm.includes('marseille') ||
    norm.includes('toulouse') ||
    norm.includes('bordeaux') ||
    norm.includes('nice')
  ) {
    return 'Europe/Paris';
  }

  // Germany
  if (
    norm.includes('germany') ||
    norm.includes('deutschland') ||
    norm.includes(', de') ||
    norm.includes('berlin') ||
    norm.includes('munich') ||
    norm.includes('münchen') ||
    norm.includes('hamburg') ||
    norm.includes('frankfurt') ||
    norm.includes('cologne') ||
    norm.includes('köln') ||
    norm.includes('düsseldorf') ||
    norm.includes('stuttgart')
  ) {
    return 'Europe/Berlin';
  }

  // UK / unrecognised — default to London
  return 'Europe/London';
};
